import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Target, Eye, Flag } from "lucide-react";

const items = [
  {
    title: "Our Mission",
    icon: Target,
    text: "Our mission is to empower students with accurate guidance, transparent counselling, and reliable overseas education solutions. We simplify admissions, improve visa success rates, and ensure placements in top international universities.",
  },
  {
    title: "Our Vision",
    icon: Eye,
    text: "Our vision is to become a globally trusted overseas education consultancy by delivering ethical guidance, world-class student support, and innovative pathways for international academic success.",
  },
  {
    title: "Our Goal",
    icon: Flag,
    text: "Our goal is to make studying abroad stress-free through complete admission assistance, scholarship guidance, visa support, and post-arrival services for long-term global careers.",
  },
];

const container = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.25 },
  },
};

const itemAnim = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function MvG() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section ref={ref} className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-6">

        {/* ===== Heading ===== */}
        <motion.div
          variants={container}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-24"
        >
          <motion.h2
            variants={itemAnim}
            className="text-4xl font-extrabold text-[#253E90]"
          >
            Our Foundation
          </motion.h2>
          <motion.p
            variants={itemAnim}
            className="mt-4 text-gray-600 max-w-2xl mx-auto"
          >
            Built on purpose, integrity, and a commitment to global student success.
          </motion.p>
        </motion.div>

        {/* ===== Pillars Layout ===== */}
        <motion.div
          variants={container}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="relative grid grid-cols-1 lg:grid-cols-3 gap-16 items-start"
        >
          {/* Vision (Left) */}
          <motion.div
            variants={itemAnim}
            className="bg-white border border-[#253E90]/20 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#253E90]/10 flex items-center justify-center mb-6">
              <Eye className="w-7 h-7 text-[#253E90]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Our Vision
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {items[1].text}
            </p>
          </motion.div>

          {/* Mission (Center – Highlighted) */}
          <motion.div
            variants={itemAnim}
            className="relative bg-[#253E90] text-white rounded-[2.5rem] p-10 shadow-2xl scale-[1.03]"
          >
            <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mb-6">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-extrabold mb-5">
              Our Mission
            </h3>
            <p className="text-white/90 text-sm leading-relaxed">
              {items[0].text}
            </p>
          </motion.div>

          {/* Goal (Right) */}
          <motion.div
            variants={itemAnim}
            className="bg-white border border-[#253E90]/20 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition"
          >
            <div className="w-14 h-14 rounded-2xl bg-[#253E90]/10 flex items-center justify-center mb-6">
              <Flag className="w-7 h-7 text-[#253E90]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Our Goal
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {items[2].text}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
